import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, LogOut, Globe, Briefcase, CalendarDays, List, Filter, X, Edit, Save, Building, MapPin, Phone, FileText, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CalendarView from "@/components/CalendarView";
import DocumentManager from "@/components/DocumentManager";
import { formatTime12, formatTimeRange } from "@/utils/timeFormat";

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, setUser } = useContext(AuthContext);
  const [interpreters, setInterpreters] = useState([]);
  const [selectedInterpreter, setSelectedInterpreter] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingNotes, setBookingNotes] = useState("");
  const [bookingLocation, setBookingLocation] = useState("");
  const [bookingMeetingType, setBookingMeetingType] = useState("in-person");
  const [bookingPaymentType, setBookingPaymentType] = useState("private-pay");
  const [bookingStartTime, setBookingStartTime] = useState("");
  const [bookingEndTime, setBookingEndTime] = useState("");
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: "",
    phone: "",
    business_name: "",
    business_address: "",
    business_phone: "",
  });
  const [broadcastData, setBroadcastData] = useState({
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    notes: "",
    meeting_type: "in-person",
    payment_type: "private-pay",
    language_needed: "",
    specialization_needed: "",
  });
  
  // Document manager state
  const [selectedBookingForDocs, setSelectedBookingForDocs] = useState(null);
  
  // Filter states
  const [languageFilter, setLanguageFilter] = useState("all");
  const [specializationFilter, setSpecializationFilter] = useState("all");
  const [allLanguages, setAllLanguages] = useState([]);
  const [allSpecializations, setAllSpecializations] = useState([]);

  useEffect(() => {
    fetchInterpreters();
    fetchMyBookings();
  }, []);

  // Initialize profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || "",
        phone: user.phone || "",
        business_name: user.business_name || "",
        business_address: user.business_address || "",
        business_phone: user.business_phone || "",
      });
    }
  }, [user]);
  
  // Extract unique languages and specializations when interpreters change
  useEffect(() => {
    const languages = new Set();
    const specializations = new Set();
    interpreters.forEach(interpreter => {
      interpreter.languages?.forEach(lang => languages.add(lang));
      interpreter.specializations?.forEach(spec => specializations.add(spec));
    });
    setAllLanguages([...languages].sort());
    setAllSpecializations([...specializations].sort());
  }, [interpreters]);
  
  // Filter interpreters based on selected filters
  const filteredInterpreters = interpreters.filter(interpreter => {
    const matchesLanguage = languageFilter === "all" || interpreter.languages?.includes(languageFilter);
    const matchesSpecialization = specializationFilter === "all" || interpreter.specializations?.includes(specializationFilter);
    return matchesLanguage && matchesSpecialization;
  });
  
  const clearFilters = () => {
    setLanguageFilter("all");
    setSpecializationFilter("all");
  };
  
  const hasActiveFilters = languageFilter !== "all" || specializationFilter !== "all";

  const fetchInterpreters = async () => {
    try {
      const result = await apiRequest(`${API}/interpreters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setInterpreters(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch interpreters:", error);
    }
  };

  const fetchInterpreterAvailability = async (interpreterId) => {
    try {
      const result = await apiRequest(`${API}/availability/interpreter/${interpreterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setAvailability(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    }
  };

  const fetchMyBookings = async () => {
    try {
      const result = await apiRequest(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.ok) {
        setMyBookings(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
    }
  };

  // Use XMLHttpRequest to avoid body stream issues
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 10000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setEditingProfile(true);

    try {
      const { ok, data } = await apiRequest(`${API}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      if (ok) {
        setUser(data);
        setShowEditProfile(false);
        toast.success("Profile updated successfully!");
      } else {
        toast.error(data.detail || "Failed to update profile");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setEditingProfile(false);
    }
  };

  const handleSelectInterpreter = (interpreter) => {
    setSelectedInterpreter(interpreter);
    fetchInterpreterAvailability(interpreter.user_id);
  };

  const handleSlotClick = (slot) => {
    if (slot.status === "available" || slot.status === "pending") {
      setSelectedSlot(slot);
      setBookingStartTime(slot.start_time);
      setBookingEndTime(slot.end_time);
      setShowBookingDialog(true);
    }
  };

  const handleBookSlot = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !selectedInterpreter) return;

    // Validate time range is within slot availability
    if (bookingStartTime < selectedSlot.start_time || bookingEndTime > selectedSlot.end_time) {
      toast.error("Selected time must be within the available slot");
      return;
    }
    if (bookingStartTime >= bookingEndTime) {
      toast.error("End time must be after start time");
      return;
    }

    try {
      const bookingPayload = {
        interpreter_id: selectedInterpreter.user_id,
        slot_id: selectedSlot.slot_id,
        date: selectedSlot.date,
        start_time: bookingStartTime,
        end_time: bookingEndTime,
        notes: bookingNotes,
        location: bookingLocation,
        meeting_type: bookingMeetingType,
        payment_type: bookingPaymentType,
      };

      // Use XMLHttpRequest to avoid body stream issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/bookings`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 15000;
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
          } catch (e) {
            resolve({ ok: false, status: xhr.status, data: { detail: "Invalid server response" } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Request timed out"));
        xhr.send(JSON.stringify(bookingPayload));
      });

      if (result.ok) {
        toast.success("Booking request submitted!");
        setShowBookingDialog(false);
        setBookingNotes("");
        setBookingLocation("");
        setBookingMeetingType("in-person");
        setBookingPaymentType("private-pay");
        setBookingStartTime("");
        setBookingEndTime("");
        fetchInterpreterAvailability(selectedInterpreter.user_id);
        fetchMyBookings();
      } else {
        toast.error(result.data.detail || "Failed to create booking");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      // Use XMLHttpRequest to avoid body stream issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", `${API}/bookings/${bookingId}/status`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 15000;
        xhr.onload = () => {
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Request timed out"));
        xhr.send(JSON.stringify({ status: "cancelled" }));
      });

      if (result.ok) {
        toast.success("Booking cancelled");
        fetchMyBookings();
        if (selectedInterpreter) {
          fetchInterpreterAvailability(selectedInterpreter.user_id);
        }
      } else {
        toast.error("Failed to cancel booking");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking? This action cannot be undone.")) {
      return;
    }
    
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("DELETE", `${API}/bookings/${bookingId}`, true);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 15000;
        xhr.onload = () => {
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Request timed out"));
        xhr.send();
      });

      if (result.ok) {
        toast.success("Booking deleted");
        fetchMyBookings();
        if (selectedInterpreter) {
          fetchInterpreterAvailability(selectedInterpreter.user_id);
        }
      } else {
        toast.error("Failed to delete booking");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleBroadcastBooking = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...broadcastData,
        language_needed: broadcastData.language_needed || null,
        specialization_needed: broadcastData.specialization_needed || null,
      };
      
      // Use XMLHttpRequest to avoid body stream issues with external scripts
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/bookings/broadcast`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 15000;
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
          } catch (e) {
            resolve({ ok: false, status: xhr.status, data: { detail: "Invalid server response" } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Request timed out"));
        xhr.send(JSON.stringify(payload));
      });

      if (result.ok) {
        toast.success(`Request sent to ${result.data.total_count} interpreter(s)!`);
        setShowBroadcastDialog(false);
        setBroadcastData({ date: "", start_time: "", end_time: "", location: "", notes: "", meeting_type: "in-person", payment_type: "private-pay", language_needed: "", specialization_needed: "" });
        fetchMyBookings();
      } else {
        toast.error(result.data.detail || "Failed to send broadcast request");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>User Dashboard</h1>
            <p className="text-slate-600 text-sm mt-1" style={{ fontFamily: 'Public Sans' }}>Welcome, {user?.full_name}</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="broadcast-booking-btn"
              onClick={() => setShowBroadcastDialog(true)}
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md px-4 py-2 font-medium"
            >
              Request from All Available
            </Button>
            <Button
              data-testid="user-logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Card */}
        <div className="card p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Your Profile</h2>
            <Button
              data-testid="edit-user-profile-btn"
              onClick={() => setShowEditProfile(true)}
              variant="outline"
              className="text-slate-600 hover:text-slate-900"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600 font-medium">Full Name</p>
              <p className="text-slate-900">{user?.full_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Email</p>
              <p className="text-slate-900">{user?.email}</p>
            </div>
            {user?.phone && (
              <div>
                <p className="text-sm text-slate-600 font-medium flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Phone
                </p>
                <p className="text-slate-900">{user.phone}</p>
              </div>
            )}
            {user?.business_name && (
              <div>
                <p className="text-sm text-slate-600 font-medium flex items-center gap-1">
                  <Building className="w-3 h-3" /> Business Name
                </p>
                <p className="text-slate-900">{user.business_name}</p>
              </div>
            )}
            {user?.business_address && (
              <div>
                <p className="text-sm text-slate-600 font-medium flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Business Address
                </p>
                <p className="text-slate-900">{user.business_address}</p>
              </div>
            )}
            {user?.business_phone && (
              <div>
                <p className="text-sm text-slate-600 font-medium flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Business Phone
                </p>
                <p className="text-slate-900">{user.business_phone}</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Interpreters List */}
          <div className="md:col-span-1">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Available Interpreters</h2>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-slate-500 hover:text-slate-700 text-xs"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              
              {/* Filter Controls */}
              <div className="space-y-3 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
                  <Filter className="w-3 h-3" />
                  Filter Interpreters
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Language</Label>
                  <Select value={languageFilter} onValueChange={setLanguageFilter}>
                    <SelectTrigger className="mt-1 h-9 text-sm" data-testid="language-filter-select">
                      <SelectValue placeholder="All Languages" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Languages</SelectItem>
                      {allLanguages.map((lang) => (
                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Specialization</Label>
                  <Select value={specializationFilter} onValueChange={setSpecializationFilter}>
                    <SelectTrigger className="mt-1 h-9 text-sm" data-testid="specialization-filter-select">
                      <SelectValue placeholder="All Specializations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Specializations</SelectItem>
                      {allSpecializations.map((spec) => (
                        <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && (
                  <p className="text-xs text-slate-500">
                    Showing {filteredInterpreters.length} of {interpreters.length} interpreters
                  </p>
                )}
              </div>
              
              <div className="space-y-3">
                {filteredInterpreters.length === 0 ? (
                  <p className="text-slate-600 text-center py-8 text-sm">
                    {hasActiveFilters ? "No interpreters match your filters" : "No interpreters available"}
                  </p>
                ) : (
                  filteredInterpreters.map((interpreter) => (
                    <div
                      key={interpreter.user_id}
                      data-testid={`interpreter-card-${interpreter.user_id}`}
                      onClick={() => handleSelectInterpreter(interpreter)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedInterpreter?.user_id === interpreter.user_id
                          ? "border-slate-900 bg-slate-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 text-sm">{interpreter.full_name}</h3>
                          <p className="text-xs text-slate-600 mt-1">
                            {interpreter.languages?.join(", ") || "No languages listed"}
                          </p>
                          {interpreter.specializations?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {interpreter.specializations.map((spec, idx) => (
                                <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                  {spec}
                                </span>
                              ))}
                            </div>
                          )}
                          {interpreter.court_certifications?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-500 font-medium">Court Certifications:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {interpreter.court_certifications.map((cert, idx) => (
                                  <span key={idx} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded" data-testid={`interpreter-cert-${idx}`}>
                                    {cert}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Calendar & Availability */}
          <div className="md:col-span-2">
            <div className="card p-6 mb-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>
                {selectedInterpreter ? `${selectedInterpreter.full_name}'s Availability` : "Select an Interpreter"}
              </h2>
              {selectedInterpreter ? (
                <Tabs defaultValue="calendar" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="calendar" data-testid="user-calendar-view-tab">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Calendar View
                    </TabsTrigger>
                    <TabsTrigger value="list" data-testid="user-list-view-tab">
                      <List className="w-4 h-4 mr-2" />
                      List View
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="calendar">
                    {availability.filter(slot => slot.status === "available").length === 0 ? (
                      <p className="text-slate-600 text-center py-12">No available slots</p>
                    ) : (
                      <CalendarView 
                        slots={availability} 
                        onSlotClick={handleSlotClick}
                        onCreateSlot={null}
                        mode="user"
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="list">
                    <div className="space-y-3">
                      {availability.filter(slot => slot.status === "available").length === 0 ? (
                        <p className="text-slate-600 text-center py-12">No available slots</p>
                      ) : (
                        availability
                          .filter(slot => slot.status === "available")
                          .map((slot) => (
                            <div
                              key={slot.slot_id}
                              data-testid={`available-slot-${slot.slot_id}`}
                              onClick={() => handleSlotClick(slot)}
                              className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 cursor-pointer hover:border-emerald-400 hover:bg-emerald-100 transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Calendar className="w-5 h-5 text-emerald-600" />
                                  <div>
                                    <p className="font-medium text-slate-900">{slot.date}</p>
                                    <p className="text-sm text-slate-600">{formatTimeRange(slot.start_time, slot.end_time)}</p>
                                  </div>
                                </div>
                                <Button data-testid={`book-slot-btn-${slot.slot_id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                  Book
                                </Button>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <p className="text-slate-600 text-center py-12">Please select an interpreter to view their availability</p>
              )}
            </div>

            {/* My Bookings */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>My Bookings</h2>
              <div className="space-y-3">
                {myBookings.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">No bookings yet</p>
                ) : (
                  myBookings.map((booking) => (
                    <div key={booking.booking_id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <Briefcase className="w-5 h-5 text-slate-600" />
                          <div>
                            <p className="font-medium text-slate-900">{booking.date}</p>
                            <p className="text-sm text-slate-600">{formatTimeRange(booking.start_time, booking.end_time)}</p>
                          </div>
                        </div>
                        <span className={`status-badge status-${booking.status}`}>
                          {booking.status}
                        </span>
                      </div>
                      {booking.meeting_type && (
                        <p className="text-sm text-slate-600 mt-2 flex items-center gap-1">
                          <span className="font-medium">Meeting Type:</span> 
                          <span className="capitalize">{booking.meeting_type.replace('-', ' ')}</span>
                        </p>
                      )}
                      {booking.payment_type && (
                        <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                          <span className="font-medium">Payment:</span> 
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            booking.payment_type === 'private-pay' ? 'bg-green-100 text-green-700' :
                            booking.payment_type === 'cja' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {booking.payment_type === 'private-pay' ? 'Private Pay' :
                             booking.payment_type === 'cja' ? 'CJA' : 'State Court'}
                          </span>
                        </p>
                      )}
                      {booking.location && (
                        <p className="text-sm text-slate-600 mt-2 flex items-center gap-1">
                          <span className="font-medium">Location:</span> {booking.location}
                        </p>
                      )}
                      {booking.notes && (
                        <p className="text-sm text-slate-600 mt-2">Notes: {booking.notes}</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          data-testid={`view-docs-${booking.booking_id}`}
                          onClick={() => setSelectedBookingForDocs(booking)}
                          size="sm"
                          variant="outline"
                          className="flex-1 border-slate-300"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Docs
                        </Button>
                        {(booking.status === "pending" || booking.status === "confirmed") && (
                          <Button
                            data-testid={`cancel-my-booking-${booking.booking_id}`}
                            onClick={() => handleCancelBooking(booking.booking_id)}
                            size="sm"
                            variant="outline"
                            className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50"
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-my-booking-${booking.booking_id}`}
                          onClick={() => handleDeleteBooking(booking.booking_id)}
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book Appointment</DialogTitle>
            <DialogDescription>
              Confirm your booking with {selectedInterpreter?.full_name}
            </DialogDescription>
          </DialogHeader>
          {selectedSlot && (
            <form onSubmit={handleBookSlot} className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">Date</p>
                <p className="font-semibold text-slate-900">{selectedSlot.date}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Interpreter available: {formatTimeRange(selectedSlot.start_time, selectedSlot.end_time)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking_start_time">Start Time *</Label>
                  <Input
                    id="booking_start_time"
                    data-testid="booking-start-time-input"
                    type="time"
                    required
                    value={bookingStartTime}
                    onChange={(e) => setBookingStartTime(e.target.value)}
                    min={selectedSlot.start_time}
                    max={selectedSlot.end_time}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="booking_end_time">End Time *</Label>
                  <Input
                    id="booking_end_time"
                    data-testid="booking-end-time-input"
                    type="time"
                    required
                    value={bookingEndTime}
                    onChange={(e) => setBookingEndTime(e.target.value)}
                    min={selectedSlot.start_time}
                    max={selectedSlot.end_time}
                    className="mt-2"
                  />
                </div>
              </div>
              <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                Select your preferred time within the interpreter's availability window
              </p>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  data-testid="booking-location-input"
                  value={bookingLocation}
                  onChange={(e) => setBookingLocation(e.target.value)}
                  placeholder="Enter meeting location (e.g., City Hospital, Room 305)"
                  className="mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">Specify where the interpretation service is needed</p>
              </div>
              <div>
                <Label htmlFor="meeting_type">Meeting Type</Label>
                <Select value={bookingMeetingType} onValueChange={setBookingMeetingType}>
                  <SelectTrigger className="mt-2" data-testid="booking-meeting-type-select">
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in-person">In Person</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="phone">Phone Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment_type">Payment Type</Label>
                <Select value={bookingPaymentType} onValueChange={setBookingPaymentType}>
                  <SelectTrigger className="mt-2" data-testid="booking-payment-type-select">
                    <SelectValue placeholder="Select payment type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private-pay">Private Pay</SelectItem>
                    <SelectItem value="cja">CJA (Criminal Justice Act)</SelectItem>
                    <SelectItem value="state-court">State Court</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="booking-notes-input"
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  placeholder="Add any special requirements or notes..."
                  className="mt-2 min-h-[100px]"
                />
              </div>
              <Button data-testid="confirm-booking-btn" type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800">
                Confirm Booking
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Broadcast Booking Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request from All Available Interpreters</DialogTitle>
            <DialogDescription>
              Send your booking request to all interpreters with availability at your chosen time
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBroadcastBooking} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>How it works:</strong> Your request will be sent to all interpreters available at the specified time. The first interpreter to confirm gets the booking.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="broadcast-date">Date *</Label>
                <Input
                  id="broadcast-date"
                  data-testid="broadcast-date-input"
                  type="date"
                  required
                  value={broadcastData.date}
                  onChange={(e) => setBroadcastData({ ...broadcastData, date: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="broadcast-start">Start Time *</Label>
                <Input
                  id="broadcast-start"
                  data-testid="broadcast-start-input"
                  type="time"
                  required
                  value={broadcastData.start_time}
                  onChange={(e) => setBroadcastData({ ...broadcastData, start_time: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="broadcast-end">End Time *</Label>
              <Input
                id="broadcast-end"
                data-testid="broadcast-end-input"
                type="time"
                required
                value={broadcastData.end_time}
                onChange={(e) => setBroadcastData({ ...broadcastData, end_time: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="broadcast-location">Location</Label>
              <Input
                id="broadcast-location"
                data-testid="broadcast-location-input"
                value={broadcastData.location}
                onChange={(e) => setBroadcastData({ ...broadcastData, location: e.target.value })}
                placeholder="Enter meeting location"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="broadcast-meeting-type">Meeting Type</Label>
              <Select 
                value={broadcastData.meeting_type} 
                onValueChange={(value) => setBroadcastData({ ...broadcastData, meeting_type: value })}
              >
                <SelectTrigger className="mt-2" data-testid="broadcast-meeting-type-select">
                  <SelectValue placeholder="Select meeting type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-person">In Person</SelectItem>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="phone">Phone Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="broadcast-payment-type">Payment Type</Label>
              <Select 
                value={broadcastData.payment_type} 
                onValueChange={(value) => setBroadcastData({ ...broadcastData, payment_type: value })}
              >
                <SelectTrigger className="mt-2" data-testid="broadcast-payment-type-select">
                  <SelectValue placeholder="Select payment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private-pay">Private Pay</SelectItem>
                  <SelectItem value="cja">CJA (Criminal Justice Act)</SelectItem>
                  <SelectItem value="state-court">State Court</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="broadcast-language">Language Needed</Label>
                <Select 
                  value={broadcastData.language_needed || "any"} 
                  onValueChange={(value) => setBroadcastData({ ...broadcastData, language_needed: value === "any" ? "" : value })}
                >
                  <SelectTrigger className="mt-2" data-testid="broadcast-language-select">
                    <SelectValue placeholder="Any language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Language</SelectItem>
                    {allLanguages.map((lang) => (
                      <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="broadcast-specialization">Specialization Needed</Label>
                <Select 
                  value={broadcastData.specialization_needed || "any"} 
                  onValueChange={(value) => setBroadcastData({ ...broadcastData, specialization_needed: value === "any" ? "" : value })}
                >
                  <SelectTrigger className="mt-2" data-testid="broadcast-specialization-select">
                    <SelectValue placeholder="Any specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Specialization</SelectItem>
                    {allSpecializations.map((spec) => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="broadcast-notes">Notes (Optional)</Label>
              <Textarea
                id="broadcast-notes"
                data-testid="broadcast-notes-input"
                value={broadcastData.notes}
                onChange={(e) => setBroadcastData({ ...broadcastData, notes: e.target.value })}
                placeholder="Add any special requirements..."
                className="mt-2 min-h-[80px]"
              />
            </div>
            <Button 
              data-testid="confirm-broadcast-btn" 
              type="submit" 
              className="w-full bg-blue-600 text-white hover:bg-blue-700"
            >
              Send Request to All Available Interpreters
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Your Profile</DialogTitle>
            <DialogDescription>Update your profile information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <Label htmlFor="edit_user_full_name">Full Name</Label>
              <Input
                id="edit_user_full_name"
                data-testid="edit-user-fullname-input"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                className="mt-2"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_user_phone">Phone Number</Label>
              <Input
                id="edit_user_phone"
                data-testid="edit-user-phone-input"
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="mt-2"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <Label htmlFor="edit_business_name">Business Name</Label>
              <Input
                id="edit_business_name"
                data-testid="edit-business-name-input"
                value={profileData.business_name}
                onChange={(e) => setProfileData({ ...profileData, business_name: e.target.value })}
                className="mt-2"
                placeholder="Your Company Name"
              />
            </div>
            <div>
              <Label htmlFor="edit_business_address">Business Address</Label>
              <Input
                id="edit_business_address"
                data-testid="edit-business-address-input"
                value={profileData.business_address}
                onChange={(e) => setProfileData({ ...profileData, business_address: e.target.value })}
                className="mt-2"
                placeholder="123 Main St, City, State, ZIP"
              />
            </div>
            <div>
              <Label htmlFor="edit_business_phone">Business Phone</Label>
              <Input
                id="edit_business_phone"
                data-testid="edit-business-phone-input"
                type="tel"
                value={profileData.business_phone}
                onChange={(e) => setProfileData({ ...profileData, business_phone: e.target.value })}
                className="mt-2"
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditProfile(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-user-profile-btn"
                type="submit"
                disabled={editingProfile}
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
              >
                {editingProfile ? "Saving..." : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Manager */}
      <DocumentManager
        bookingId={selectedBookingForDocs?.booking_id}
        isOpen={!!selectedBookingForDocs}
        onClose={() => setSelectedBookingForDocs(null)}
        bookingDate={selectedBookingForDocs?.date}
      />
    </div>
  );
};

export default UserDashboard;